export type InsightsCoverageStatus =
  "connected" | "expired" | "missing" | "live" | "thin" | "dead" | "blocked" | "unknown";

export type InsightsCoveragePlatform = {
  id: string;
  label: string;
  status: InsightsCoverageStatus;
  detail?: string;
  needsFix?: boolean;
};

export type InsightsCoverageSnapshot = {
  platforms: InsightsCoveragePlatform[];
  updatedAt?: string;
};

function slugifyId(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function titleCaseLabel(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  if (/[a-z]/.test(trimmed) && /[A-Z]/.test(trimmed)) return trimmed;
  return trimmed
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function needsFixForStatus(status: string): boolean {
  return status === "blocked" || status === "expired" || status === "missing";
}

function statusFromConnectionRow(
  statusRaw: string,
  isConnected: boolean | undefined,
): InsightsCoverageStatus {
  const exact = statusRaw.trim().toLowerCase();
  if (
    exact === "connected" ||
    exact === "expired" ||
    exact === "missing" ||
    exact === "blocked" ||
    exact === "unknown" ||
    exact === "live" ||
    exact === "thin" ||
    exact === "dead"
  ) {
    return exact;
  }
  if (isConnected === false) return "missing";
  if (isConnected === true || exact === "active") return "connected";
  return "unknown";
}

const ALLOWED: Set<string> = new Set([
  "connected",
  "expired",
  "missing",
  "live",
  "thin",
  "dead",
  "blocked",
  "unknown",
]);

function normalizeStatus(raw: string | undefined): InsightsCoverageStatus {
  if (!raw?.trim()) return "unknown";
  const s = raw.trim().toLowerCase();
  return ALLOWED.has(s) ? (s as InsightsCoverageStatus) : "unknown";
}

/**
 * Preserve census terrain (live/thin/dead/blocked) when a later write only has
 * OAuth/estate placeholders (connected/unknown) — e.g. ETL coverage_platforms.
 */
export function resolveCoverageStatus(
  incoming: string | undefined,
  existing?: string,
): InsightsCoverageStatus {
  const next = normalizeStatus(incoming);
  const prev = normalizeStatus(existing);
  const nextWeak = next === "connected" || next === "unknown";
  const prevTerrain = prev === "live" || prev === "thin" || prev === "dead" || prev === "blocked";
  if (nextWeak && prevTerrain) return prev;
  return next;
}

/** Connection-catalog rows (OAuth enrichment; not the strip alone). */
export function parseConnectionsCoverage(
  result: unknown,
  onlyIds?: Set<string>,
): InsightsCoveragePlatform[] {
  const rows = Array.isArray(result)
    ? result
    : result &&
        typeof result === "object" &&
        Array.isArray((result as { connections?: unknown }).connections)
      ? (result as { connections: unknown[] }).connections
      : result && typeof result === "object" && Array.isArray((result as { items?: unknown }).items)
        ? (result as { items: unknown[] }).items
        : [];

  const out: InsightsCoveragePlatform[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const platform =
      (typeof r.platform === "string" && r.platform) ||
      (typeof r.slug === "string" && r.slug) ||
      "";
    const name =
      (typeof r.name === "string" && r.name) ||
      (typeof r.displayName === "string" && r.displayName) ||
      "";
    if (!platform && !name) continue;

    const id = slugifyId(platform || name);
    if (!id) continue;
    if (onlyIds && !onlyIds.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);

    const status = statusFromConnectionRow(
      String((r as { connectionState?: unknown }).connectionState ?? r.status ?? ""),
      typeof r.isConnected === "boolean" ? r.isConnected : undefined,
    );

    out.push({
      id,
      label: name ? titleCaseLabel(name) : titleCaseLabel(platform || id),
      status,
      detail: typeof r.status === "string" ? r.status : undefined,
      needsFix: needsFixForStatus(status),
    });
  }
  return out;
}

export function enrichCoverageFromCatalog(
  selected: InsightsCoveragePlatform[],
  catalog: InsightsCoveragePlatform[],
): InsightsCoveragePlatform[] {
  if (!selected.length) return [];
  const byId = new Map(catalog.map((p) => [p.id, p]));
  return selected.map((u) => {
    const c = byId.get(u.id);
    if (!c) return { ...u, needsFix: needsFixForStatus(u.status) };
    const status: InsightsCoverageStatus =
      u.status !== "unknown" && u.status !== "connected" ? u.status : c.status;
    return {
      ...c,
      ...u,
      label: c.label || u.label,
      status,
      detail: u.detail ?? c.detail,
      needsFix: needsFixForStatus(status),
    };
  });
}

export type CoverageUpdateInput = {
  id?: string;
  platform?: string;
  connection_id?: string;
  status?: string;
  detail?: string;
  label?: string;
};

export function platformsFromCoverageUpdate(
  platforms: CoverageUpdateInput[],
  catalog: InsightsCoveragePlatform[] = [],
): InsightsCoveragePlatform[] {
  const byId = new Map(catalog.map((p) => [p.id, p]));
  const byLabel = new Map(catalog.map((p) => [slugifyId(p.label), p]));
  const out: InsightsCoveragePlatform[] = [];
  const seen = new Set<string>();

  for (const raw of platforms) {
    const hint =
      (typeof raw.platform === "string" && raw.platform.trim()) ||
      (typeof raw.id === "string" && raw.id.trim()) ||
      "";
    if (!hint && !raw.connection_id) continue;

    const slug = hint ? slugifyId(hint) : "";
    const fromCatalog = (slug && byId.get(slug)) || (slug && byLabel.get(slug)) || undefined;
    const id = fromCatalog?.id ?? slug;
    if (!id || seen.has(id)) continue;
    seen.add(id);

    const status =
      normalizeStatus(raw.status) !== "unknown"
        ? normalizeStatus(raw.status)
        : (fromCatalog?.status ?? "unknown");

    out.push({
      id,
      label:
        (typeof raw.label === "string" && raw.label.trim()) ||
        fromCatalog?.label ||
        titleCaseLabel(hint || id),
      status,
      detail: raw.detail?.trim() || fromCatalog?.detail,
      needsFix: needsFixForStatus(status),
    });
  }
  return out;
}

export function platformsFromEtlArgs(args: unknown): InsightsCoveragePlatform[] {
  if (!args || typeof args !== "object") return [];
  const a = args as Record<string, unknown>;
  const inputs: CoverageUpdateInput[] = [];

  const pushBranch = (b: Record<string, unknown>) => {
    const platform =
      (typeof b.platform === "string" && b.platform) || (typeof b.app === "string" && b.app) || "";
    if (!platform) return;
    inputs.push({
      platform,
      connection_id: typeof b.connection_id === "string" ? b.connection_id : undefined,
      status: "connected",
    });
  };

  if (Array.isArray(a.branches)) {
    for (const b of a.branches) {
      if (b && typeof b === "object") pushBranch(b as Record<string, unknown>);
    }
  }
  if (typeof a.platform === "string" && a.platform.trim()) {
    pushBranch(a);
  }

  return platformsFromCoverageUpdate(inputs);
}

export function platformsFromCoverageToolResult(result: unknown): InsightsCoveragePlatform[] {
  if (!result || typeof result !== "object") return [];
  const r = result as Record<string, unknown>;
  if (r.status === "error") return [];
  const platforms = r.platforms;
  if (!Array.isArray(platforms)) return [];
  return platformsFromCoverageUpdate(
    platforms.map((p) => {
      if (!p || typeof p !== "object") return {};
      const row = p as Record<string, unknown>;
      return {
        id: typeof row.id === "string" ? row.id : undefined,
        platform: typeof row.platform === "string" ? row.platform : undefined,
        label: typeof row.label === "string" ? row.label : undefined,
        status: typeof row.status === "string" ? row.status : undefined,
        detail: typeof row.detail === "string" ? row.detail : undefined,
        connection_id: typeof row.connection_id === "string" ? row.connection_id : undefined,
      };
    }),
  );
}

/** Rebuild selected estate from tool activity (no ledger). */
export function coverageFromToolActivity(
  items: Array<{
    kind: string;
    activity?: { tool: string; phase: string; args?: unknown; result?: unknown };
  }>,
): InsightsCoverageSnapshot | null {
  let catalog: InsightsCoveragePlatform[] = [];
  let selected: InsightsCoveragePlatform[] = [];

  for (const item of items) {
    if (item.kind !== "tool" || !item.activity) continue;
    const { tool, phase, args, result } = item.activity;

    if (phase === "result" && tool === "list_connections") {
      catalog = parseConnectionsCoverage(result);
    }

    if (phase === "call" && tool === "start_investigation_etl") {
      selected = mergePlatforms(selected, platformsFromEtlArgs(args));
    }

    if (phase === "result" && tool === "start_investigation_etl") {
      const cov =
        result && typeof result === "object"
          ? (result as { coverage_platforms?: unknown }).coverage_platforms
          : undefined;
      if (Array.isArray(cov) && cov.length) {
        const fromEtl = platformsFromCoverageUpdate(
          cov.map((p) => {
            if (!p || typeof p !== "object") return {};
            const row = p as Record<string, unknown>;
            return {
              id: typeof row.id === "string" ? row.id : undefined,
              platform: typeof row.platform === "string" ? row.platform : undefined,
              status: typeof row.status === "string" ? row.status : "connected",
            };
          }),
          catalog,
        );
        const mode =
          args && typeof args === "object" && typeof (args as { mode?: unknown }).mode === "string"
            ? (args as { mode: string }).mode
            : undefined;
        selected =
          mode === "gather"
            ? carryTerrainIntoReplace(fromEtl, selected)
            : mergePlatforms(selected, fromEtl);
      }
    }

    if (phase === "result" && tool === "update_investigation_coverage") {
      const platforms = platformsFromCoverageToolResult(result);
      const replace =
        result && typeof result === "object" && (result as { replace?: boolean }).replace === true;
      if (isFullCatalogCoverageDump(platforms, catalog)) {
        continue;
      }
      selected = replace
        ? carryTerrainIntoReplace(platforms, selected)
        : mergePlatforms(selected, platforms);
    }
  }

  if (!selected.length) return null;
  return {
    platforms: enrichCoverageFromCatalog(selected, catalog),
    updatedAt: new Date().toISOString(),
  };
}

function carryTerrainIntoReplace(
  next: InsightsCoveragePlatform[],
  previous: InsightsCoveragePlatform[],
): InsightsCoveragePlatform[] {
  if (!previous.length) return next;
  const prevById = new Map(previous.map((p) => [p.id, p]));
  return next.map((p) => {
    const prior = prevById.get(p.id);
    if (!prior) return p;
    const status = resolveCoverageStatus(p.status, prior.status);
    return {
      ...p,
      status,
      detail: p.detail ?? prior.detail,
      needsFix: needsFixForStatus(status),
    };
  });
}

function mergePlatforms(
  prev: InsightsCoveragePlatform[],
  next: InsightsCoveragePlatform[],
): InsightsCoveragePlatform[] {
  const byId = new Map(prev.map((p) => [p.id, { ...p }]));
  for (const p of next) {
    const existing = byId.get(p.id);
    if (!existing) {
      byId.set(p.id, { ...p });
      continue;
    }
    const status = resolveCoverageStatus(p.status, existing.status);
    byId.set(p.id, {
      ...existing,
      ...p,
      status,
      detail: p.detail ?? existing.detail,
      needsFix: needsFixForStatus(status),
    });
  }
  return [...byId.values()];
}

/** True when update dumped the entire connection catalog as selected estate. */
export function isFullCatalogCoverageDump(
  platforms: InsightsCoveragePlatform[],
  catalog: InsightsCoveragePlatform[],
): boolean {
  if (catalog.length < 4 || platforms.length < catalog.length) return false;
  const selectedIds = new Set(platforms.map((p) => p.id));
  return catalog.every((c) => selectedIds.has(c.id));
}

/** Merge coverage snapshots (later parts refine status/detail). */
export function mergeCoverageSnapshots(
  ...parts: Array<InsightsCoverageSnapshot | null | undefined>
): InsightsCoverageSnapshot {
  const byId = new Map<string, InsightsCoveragePlatform>();
  for (const part of parts) {
    if (!part) continue;
    for (const p of part.platforms) {
      const prev = byId.get(p.id);
      if (!prev) {
        byId.set(p.id, { ...p });
        continue;
      }
      byId.set(p.id, {
        ...prev,
        ...p,
        detail: p.detail ?? prev.detail,
        status: resolveCoverageStatus(p.status, prev.status),
        needsFix: needsFixForStatus(resolveCoverageStatus(p.status, prev.status)),
      });
    }
  }
  return {
    platforms: [...byId.values()],
    updatedAt: new Date().toISOString(),
  };
}
